/**
 * Empty State Component
 *
 * Provides consistent empty states with actionable CTAs throughout the app.
 */

import React from 'react'
import { LucideIcon } from 'lucide-react'
import { Button } from '../ui/button'

interface EmptyStateProps {
  /**
   * Icon to display
   */
  icon: LucideIcon

  /**
   * Title text
   */
  title: string

  /**
   * Description text
   */
  description?: string

  /**
   * Primary action button
   */
  action?: {
    label: string
    onClick: () => void
    variant?: 'primary' | 'secondary'
  }

  /**
   * Secondary action button
   */
  secondaryAction?: {
    label: string
    onClick: () => void
  }

  /**
   * Custom content to display
   */
  children?: React.ReactNode

  /**
   * Size variant
   */
  size?: 'sm' | 'md' | 'lg'
}

/**
 * EmptyState - Display when there's no content
 */
export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  secondaryAction,
  children,
  size = 'md',
}: EmptyStateProps) {
  const sizeClasses = {
    sm: {
      icon: 'w-12 h-12',
      title: 'text-lg',
      description: 'text-sm',
      padding: 'py-8',
    },
    md: {
      icon: 'w-16 h-16',
      title: 'text-xl',
      description: 'text-base',
      padding: 'py-12',
    },
    lg: {
      icon: 'w-20 h-20',
      title: 'text-2xl',
      description: 'text-lg',
      padding: 'py-16',
    },
  }

  const classes = sizeClasses[size]

  return (
    <div className={`text-center ${classes.padding}`}>
      <div className="flex justify-center mb-4">
        <div className={`${classes.icon} text-gray-400 dark:text-gray-500`}>
          <Icon className="w-full h-full" />
        </div>
      </div>

      <h3 className={`${classes.title} font-semibold text-gray-900 dark:text-white mb-2`}>
        {title}
      </h3>

      {description && (
        <p
          className={`${classes.description} text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto`}
        >
          {description}
        </p>
      )}

      {children && <div className="mb-6">{children}</div>}

      {(action || secondaryAction) && (
        <div className="flex items-center justify-center gap-3">
          {action && (
            <Button
              onClick={action.onClick}
              variant={action.variant === 'secondary' ? 'outline' : 'default'}
            >
              {action.label}
            </Button>
          )}

          {secondaryAction && (
            <Button onClick={secondaryAction.onClick} variant="outline">
              {secondaryAction.label}
            </Button>
          )}
        </div>
      )}
    </div>
  )
}

export default EmptyState
